import {
  type Abi,
  type Address,
  createPublicClient,
  createWalletClient,
  custom,
} from 'viem';
import { mainnet, sepolia } from 'viem/chains';
import { useActiveWeb3React } from './hooks';

const withWalletExtension = (callback: () => any) => {
  if (!('ethereum' in window) || !window.ethereum) {
    return null;
  }
  return callback();
};

const testnetClient = withWalletExtension(() =>
  createPublicClient({
    chain: sepolia,
    // transport: http('/rpc/sepolia'),
    transport: custom(window.ethereum as unknown as any),
  }),
);

const mainnetClient = withWalletExtension(() =>
  createPublicClient({
    chain: mainnet,
    // transport: http('/rpc/mainnet'),
    transport: custom(window.ethereum as unknown as any),
  }),
);

const testnetWalletClient = withWalletExtension(() =>
  createWalletClient({
    chain: sepolia,
    transport: custom(window.ethereum as unknown as any),
  }),
);

const mainnetWalletClient = withWalletExtension(() =>
  createWalletClient({
    chain: mainnet,
    transport: custom(window.ethereum as unknown as any),
  }),
);

export const viemClient = {
  [sepolia.id]: {
    public: testnetClient,
    wallet: testnetWalletClient,
  },
  [mainnet.id]: {
    public: mainnetClient,
    wallet: mainnetWalletClient,
  },
};

const allowedChainIds = Object.keys(viemClient).map(Number);
export const isAllowedChainId = (
  chainId: number,
): chainId is keyof typeof viemClient => allowedChainIds.includes(chainId);

export type PublicViemClient = typeof mainnetClient | typeof testnetClient;
export type WalletViemClient =
  | typeof mainnetWalletClient
  | typeof testnetWalletClient;
export type ViemContract = {
  address: Address | undefined | null;
  abi: Abi;
};

export function usePublicClient() {
  const { chainId } = useActiveWeb3React();

  if (!chainId || !isAllowedChainId(chainId)) {
    return null;
  }

  return viemClient[chainId].public;
}

export function useWalletClient() {
  const { chainId } = useActiveWeb3React();
  if (!('ethereum' in window) || !window.ethereum) {
    return null;
  }

  if (!chainId || !isAllowedChainId(chainId)) {
    return null;
  }

  return viemClient[chainId].wallet;
}
